import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import '@monrdv/ui/tokens.css';
import './styles/field.css';
import { router } from './router.js';
import { loadSession, type Session } from './lib/api.js';
import { requestPersistence } from './lib/localdb.js';
import { startAutoSync } from './lib/sync.js';
import { SignInScreen } from './screens/SignIn.js';
import { setLocale } from './lib/i18n.js';

/**
 * Starting up.
 *
 * The order here is the offline contract in miniature, and it is the opposite of a normal SPA:
 *
 *   1. **Ask for persistent storage first.** Without it the browser may evict the working set
 *      under pressure — and with it a technician's unsent hours and photographs. That is the
 *      worst failure this app can have, so it is asked for before anything else and the answer
 *      is shown rather than assumed.
 *   2. **Render from the local database.** Not from a fetch. A cold start in an underground
 *      garage must produce the same day as one in the street.
 *   3. **Sync in the background.** The network refills the database and drains the queue; it
 *      never gates a screen.
 *
 * The one thing that does gate the app is having a session at all, because without one there
 * is no business to build a path for. That check is a single request and its failure mode is
 * the sign-in screen, not a blank page.
 */
function App() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);

  useEffect(() => {
    void (async () => {
      // Requested before the first render that matters, because Chrome grants it far more
      // readily to an installed PWA in its first moments than to one that has been idle.
      await requestPersistence();
      const resolved = await loadSession();
      // The technician's own interface language, which is not the customer's — anything shown
      // to a customer renders from `customers.locale` instead.
      if (resolved) setLocale(resolved.locale);
      setSession(resolved);
    })();
  }, []);

  useEffect(() => {
    if (!session) return;
    // Push, then upload, then pull — and again on reconnect, on foreground, and every five
    // minutes as the backstop. The socket in the shell covers what five minutes is too slow for.
    return startAutoSync(() => {});
  }, [session]);

  if (session === undefined) {
    return <div className="loading" aria-busy="true" aria-label="Chargement" />;
  }

  if (session === null || session.tenantSlug === null) {
    return <SignInScreen onSignedIn={() => void loadSession().then(setSession)} />;
  }

  return <RouterProvider router={router} />;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
